import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { createQuote } from '../utils/quoter/quoteCreator';
import { useAuth } from '../hooks/auth/useAuth';
import logger from '../utils/logging/logger';

const componentName = 'CreateQuote';

/**
 * CreateQuote Component
 * 
 * This component serves as a bridge between SEO static pages and the quote system.
 * It accepts all necessary parameters from the URL, creates a quote via API,
 * then redirects to the sail options page with the new quote ID.
 * 
 * URL Parameters:
 * - boatId: Numeric boat identifier
 * - sailId: Sail type ID
 * - sailArea: Sail area in square feet
 * - sailCutId: Sail cut type ID
 * - isFurling: 0 or 1 indicating furling capability
 */
const CreateQuote: React.FC = () => {
    const { boatId, sailId, sailArea, sailCutId, isFurling } = useParams<{
        boatId: string;
        sailId: string;
        sailArea: string;
        sailCutId: string;
        isFurling: string;
    }>();
    
    const navigate = useNavigate();
    const { user, isInitializing } = useAuth();
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        // Wait for auth to initialize
        if (isInitializing) {
            return;
        }

        // Validate required parameters
        if (!boatId || !sailId || !sailArea || !sailCutId || isFurling === undefined) {
            logger.log('LogError', componentName, 'Missing required parameters', {
                boatId, sailId, sailArea, sailCutId, isFurling
            });
            setError('Missing required parameters. Please try again from the boat selection page.');
            return;
        }

        const createQuoteAndNavigate = async () => {
            try {
                logger.log('LogVerbose', componentName, 'Creating quote with parameters', {
                    boatId, sailId, sailArea, sailCutId, isFurling, userId: user.userId
                });

                // Create the quote using the centralized quote creator
                const newQuoteId = await createQuote(
                    parseInt(boatId),
                    parseInt(sailId),
                    sailArea,
                    sailCutId,
                    parseInt(isFurling),
                    user.userId
                );

                if (newQuoteId > 0) {
                    logger.log('LogVerbose', componentName, 'Quote created successfully', { quoteId: newQuoteId });
                    
                    // Navigate to sail options page with the new quote ID
                    navigate(
                        `/quoter/sail-options-select/${boatId}/${sailId}/${sailArea}/${newQuoteId}/${sailCutId}/${isFurling}`
                    );
                } else {
                    logger.log('LogError', componentName, 'Failed to create quote - invalid quote ID returned');
                    setError('Failed to create quote. Support has been notified. Please contact us via email or try again later.');
                }
            } catch (err) {
                logger.log('LogError', componentName, 'Error creating quote', { error: err });
                console.error('Error creating quote:', err);
                setError('Failed to create quote. Please try again.');
            }
        };

        createQuoteAndNavigate();
    }, [boatId, sailId, sailArea, sailCutId, isFurling, user.userId, navigate, isInitializing]);

    // Show error state if something went wrong
    if (error) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="400px"
                p={3}
            >
                <Typography color="error" variant="h6" gutterBottom>
                    Error Creating Quote
                </Typography>
                <Typography color="text.secondary" align="center" sx={{ mb: 3 }}>
                    {error}
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate('/quoter')}
                >
                    Return to Boat Selection
                </Button>
            </Box>
        );
    }

    // Show loading state while creating quote
    return (
        <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="400px"
        >
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 3 }}>
                Loading sail options...
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
                Setting up your configuration
            </Typography>
        </Box>
    );
};

export default CreateQuote;
